/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import type {
  Concept,
  DataExplorerModuleState,
  DataExplorerStoreType,
  Queryable,
  QueryableConfiguration,
} from './types';

export const getDataExplorerState = (
  state: DataExplorerModuleState,
): DataExplorerStoreType | undefined => {
  return state.dataExplorer;
};

export const getSearchFilter = createSelector(
  getDataExplorerState,
  (store): string => store?.filters.searchFilter || '',
);

export const getQueryables = createSelector(
  getDataExplorerState,
  (store): Queryable[] => store?.filters.queryables || [],
);

export const getQueryableConfigurations = createSelector(
  getDataExplorerState,
  (store): QueryableConfiguration[] =>
    store?.filters.queryableConfigurations || [],
);

export const getConcepts = createSelector(
  getDataExplorerState,
  (store): Concept[] => store?.filters.concepts || [],
);

export const getSelectedDatasetId = createSelector(
  getDataExplorerState,
  (store): string => store?.selectedDatasetId || '',
);

export const getDiscoveryCatalogueURL = createSelector(
  getDataExplorerState,
  (store): string => store?.discoveryCatalogueURL || '',
);

export const getWMSbox = createSelector(
  getDataExplorerState,
  (store): number[] => store?.filters.wmsbox || [],
);

export const getBbox = createSelector(
  getDataExplorerState,
  (store): number[] => store?.filters.bbox || [],
);

export const getAreaFilterTileURL = createSelector(
  getDataExplorerState,
  (store): string => store?.areaFilterTileURL || '',
);

export const getAreaFilterTileLicense = createSelector(
  getDataExplorerState,
  (store): string => store?.areaFilterTileLicense || '',
);

export const getMeteogateOnly = createSelector(
  getDataExplorerState,
  (store): boolean =>
    store?.showMeteoGateBranding !== false &&
    Boolean(store?.filters.meteogateOnly),
);

export const getShowMeteoGateBranding = createSelector(
  getDataExplorerState,
  (store): boolean => store?.showMeteoGateBranding !== false,
);
